import React from "react";
import { Helmet } from "react-helmet-async";

const CANON = "https://www.organichealing.co.uk";

export default function SEO({ title, description, path = "/", image, jsonLd, noindex = false }) {
  const url = `${CANON}${path}`;
  const t = title || "Organic Healing UK | Premium Honey & Natural Supplements";
  const d = description || "Shop premium Sidr honey, natural botanicals and food supplements from Organic Healing UK. Secure checkout, UK delivery and collection available.";
  const img = image || `${CANON}/og-image.png`;
  return (
    <Helmet>
      <title>{t}</title>
      <meta name="description" content={d} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Organic Healing" />
      <meta property="og:title" content={t} />
      <meta property="og:description" content={d} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t} />
      <meta name="twitter:description" content={d} />
      <meta name="twitter:image" content={img} />
      {Array.isArray(jsonLd) ? jsonLd.map((j, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(j)}</script>
      )) : jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}

export const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Organic Healing Ltd",
  "alternateName": "Organic Healing",
  "url": CANON,
  "logo": "https://customer-assets-v7afamib.emergentagent.net/job_healing-shop-2/artifacts/51ow55n8_logo2.png",
  "sameAs": [
    "https://www.instagram.com/organichealinguk",
    "https://www.tiktok.com/@organichealinguk"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+44 7877 855305",
    "email": "organichealing2024@gmail.com",
    "contactType": "customer service",
    "areaServed": "GB",
    "availableLanguage": "en"
  }
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${CANON}/#localbusiness`,
  "name": "Organic Healing Ltd",
  "image": "https://customer-assets-v7afamib.emergentagent.net/job_healing-shop-2/artifacts/51ow55n8_logo2.png",
  "url": CANON,
  "telephone": "+44 7877 855305",
  "email": "organichealing2024@gmail.com",
  "priceRange": "££",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Unit 13D, 47 Whitmore Road",
    "addressLocality": "Small Heath, Birmingham",
    "postalCode": "B10 0NR",
    "addressCountry": "GB"
  },
  "openingHoursSpecification": [
    {"@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], "opens": "10:00", "closes": "18:00"}
  ]
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Organic Healing",
  "url": CANON,
  "potentialAction": {
    "@type": "SearchAction",
    "target": `${CANON}/shop?q={search_term_string}`,
    "query-input": "required name=search_term_string"
  }
};

export function productJsonLd(p, priceGBP) {
  const inStock = (p.available_stock || 0) > 0;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": p.name,
    "description": p.short_description || p.description,
    "image": p.images && p.images.length ? p.images : undefined,
    "sku": p.sku || p.slug,
    "brand": {"@type": "Brand", "name": "Organic Healing"},
    "category": p.category,
    "offers": {
      "@type": "Offer",
      "url": `${CANON}/product/${p.slug}`,
      "priceCurrency": "GBP",
      "price": priceGBP.toFixed(2),
      "availability": inStock ? "https://schema.org/InStock" : (p.status === "coming_soon" ? "https://schema.org/PreOrder" : "https://schema.org/OutOfStock"),
      "itemCondition": "https://schema.org/NewCondition"
    }
  };
}
